import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["titleInput", "typeInput", "contentInput"]
  static values = {
    activityId: String,
    url: String,
    persona: String
  }

  connect() {
    // Focus input when modal opens
    setTimeout(() => {
      if (this.hasTitleInputTarget) {
        this.titleInputTarget.focus()
      }
    }, 100)
  }

  handleKeydown(event) {
    // Only trigger save on Enter for title input, not content textarea
    if (event.key === "Enter" && event.target === this.titleInputTarget) {
      event.preventDefault()
      this.save()
    }
  }

  save() {
    const sectionTitle = this.titleInputTarget.value.trim()
    const sectionContent = this.hasContentInputTarget ? this.contentInputTarget.value.trim() : ""

    if (!sectionTitle) {
      // Show error toast
      if (window.toastController) {
        window.toastController.error("Please enter a section title")
      }
      return
    }

    if (!sectionContent) {
      // Show error toast
      if (window.toastController) {
        window.toastController.error("Please enter section content")
      }
      return
    }

    // Save to backend
    this.saveToBackend(sectionTitle, sectionContent)

    // Close modal - modal is a descendant of this.element, not an ancestor
    const modal = this.element.querySelector("[data-controller~='modal']")
    if (modal) {
      const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
      if (modalController) {
        modalController.close()
      }
    }

    // Clear inputs
    this.titleInputTarget.value = ""
    if (this.hasContentInputTarget) {
      this.contentInputTarget.value = ""
    }
  }

  saveToBackend(sectionTitle, sectionContent) {
    // Get section type from select input
    const sectionType = this.hasTypeInputTarget ? this.typeInputTarget.value : "text"

    // Prepare content based on selected type
    const lines = sectionContent.split('\n').filter(line => line.trim() !== '')
    const isListType = sectionType === "list"

    const payload = {
      section_title: sectionTitle,
      section_type: sectionType,
      section_content: isListType ? lines : sectionContent
    }

    // Add persona if this is a persona-specific section
    if (this.personaValue && this.personaValue.trim() !== "") {
      payload.persona = this.personaValue
    }

    fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
      },
      body: JSON.stringify(payload)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(() => {
            throw new Error(`HTTP ${response.status}`)
          })
        }
        return response.json()
      })
      .then(data => {
        if (data.status === "success") {
          // Section saved successfully
          if (window.toastController) {
            const personaText = data.persona ? ` to ${data.persona}` : ""
            window.toastController.success(`Section "${data.section_title}" added successfully${personaText}`)
          }

          // Insert new section into DOM without page reload
          this.insertNewSection(data)
        } else if (data.error) {
          if (window.toastController) {
            window.toastController.error(data.error)
          }
        }
      })
      .catch(error => {
        if (window.toastController) {
          window.toastController.error("Failed to save section")
        }
      })
  }

  insertNewSection(data) {
    const { section_title, section_value, section_type, persona } = data
    const isListType = section_type === "list"

    // Create section HTML
    const sectionHtml = this.createSectionHtml(section_title, section_value, isListType)

    // Find the container to insert into
    let container
    if (persona) {
      // Find persona-specific container
      // Look for the persona section that contains this add-section controller
      const personaSection = this.element.closest('.flex.flex-col.gap-4')
      if (personaSection) {
        // Insert before the "Add section" button container
        const addButtonContainer = this.element
        addButtonContainer.insertAdjacentHTML('beforebegin', sectionHtml)
      }
    } else {
      // Find general sections container
      // Look for the general sections area
      const generalContainer = this.element.closest('.flex.flex-col.gap-4')
      if (generalContainer) {
        // Insert before the "Add section" button container
        const addButtonContainer = this.element
        addButtonContainer.insertAdjacentHTML('beforebegin', sectionHtml)
      }
    }
  }

  createSectionHtml(title, value, isList) {
    const contentHtml = isList
      ? `<ul class="list-disc list-inside space-y-2 font-normal text-sm leading-5 text-[#374151]">
           ${Array.isArray(value) ? value.map(item => `<li>${this.escapeHtml(item)}</li>`).join('') : ''}
         </ul>`
      : `<p class="font-normal text-sm leading-5 text-[#374151]">${this.escapeHtml(value)}</p>`

    return `
      <div class="flex flex-col gap-3 p-5 bg-white border border-[#E4E5E8] rounded-lg">
        <div class="flex items-center justify-between">
          <span class="font-semibold text-base leading-6 text-[#030712]">${this.escapeHtml(title)}</span>
          <div class="flex gap-1">
            <button type="button" class="inline-flex items-center justify-center gap-2 font-semibold transition-all border disabled:cursor-not-allowed rounded-md text-sm px-3.5 py-2 h-9 bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 disabled:opacity-50 disabled:pointer-events-none" data-action="click->guideline-editor#editSection">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.99902 17.2512V21.0012H6.74902L17.809 9.94125L14.059 6.19125L2.99902 17.2512Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M20.71 7.04126C21.1 6.65126 21.1 6.02126 20.71 5.63126L18.37 3.29126C17.98 2.90126 17.35 2.90126 16.96 3.29126L15.13 5.12126L18.88 8.87126L20.71 7.04126Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <button type="button" class="inline-flex items-center justify-center gap-2 font-semibold transition-all border disabled:cursor-not-allowed rounded-md text-sm px-3.5 py-2 h-9 bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 disabled:opacity-50 disabled:pointer-events-none" data-action="click->guideline-editor#deleteSection">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 6H5H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
        </div>
        ${contentHtml}
      </div>
    `
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
